<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import DialerAPI from 'dashboard/api/campaigns/dialer';
import CampaignsAPI from 'dashboard/api/campaigns';
import { useDialerPresenceStore } from 'dashboard/stores/dialerPresence';

import CallStatsPanel from 'dashboard/components-next/Calls/CallStatsPanel.vue';
import VoiceCampaignQueueList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/VoiceCampaign/VoiceCampaignQueueList.vue';
import VoiceCampaignDiagnostics from 'dashboard/components-next/Campaigns/Pages/CampaignPage/VoiceCampaign/VoiceCampaignDiagnostics.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Named for the campaigns view, which keeps this page out of its cache.
defineOptions({ name: 'VoiceCampaignDetailPage' });

const STATE_POLL_MS = 5000;

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
// An agent works the campaign: they enter it, take calls and watch it run. Who gets called is a
// decision about the operation, and the server refuses it from anyone but an administrator - so the
// screen does not offer it either.
const { isAdmin } = useAdmin();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);

// An agent cannot list the account's campaigns, so the store never holds theirs: the one they work
// is fetched on its own, from the endpoint that answers which campaigns they may enter.
const ownCampaign = ref(null);

const campaign = computed(
  () =>
    getters['campaigns/getVoiceCampaigns'].value.find(
      item => String(item.id) === String(route.params.campaignId)
    ) || ownCampaign.value
);

const fetchOwnCampaign = async () => {
  if (isAdmin.value || campaign.value) return;

  const { data } = await CampaignsAPI.dialable();
  ownCampaign.value = data.find(
    item => String(item.id) === String(route.params.campaignId)
  );
};

const dialerConfig = computed(() => campaign.value?.dialer_config || {});

const audienceSummary = computed(() => {
  const audience = campaign.value?.audience || [];
  const [first] = audience;
  if (!first) return t('CAMPAIGN.VOICE.DETAIL.AUDIENCE.NONE');
  if (first.type === 'AllContacts') {
    return t('CAMPAIGN.VOICE.DETAIL.AUDIENCE.ALL_CONTACTS');
  }
  if (first.type === 'ContactIds') {
    const count = first.ids?.length || 0;
    return t('CAMPAIGN.VOICE.DETAIL.AUDIENCE.LIST', { count }, count);
  }
  if (first.type === 'Inbox') {
    return t('CAMPAIGN.VOICE.DETAIL.AUDIENCE.INBOX');
  }
  return t(
    'CAMPAIGN.VOICE.DETAIL.AUDIENCE.LABELS',
    { count: audience.length },
    audience.length
  );
});

const rules = computed(() => [
  {
    key: 'attempts',
    label: t('CAMPAIGN.VOICE.DETAIL.RULES.ATTEMPTS'),
    value: dialerConfig.value.max_attempts ?? 3,
  },
  {
    key: 'interval',
    label: t('CAMPAIGN.VOICE.DETAIL.RULES.INTERVAL'),
    value: t('CAMPAIGN.VOICE.DETAIL.RULES.HOURS', {
      count: dialerConfig.value.retry_interval_hours ?? 4,
    }),
  },
  {
    key: 'wrapUp',
    label: t('CAMPAIGN.VOICE.DETAIL.RULES.WRAP_UP'),
    value: t('CAMPAIGN.VOICE.DETAIL.RULES.SECONDS', {
      count: dialerConfig.value.wrap_up_seconds ?? 20,
    }),
  },
  {
    key: 'lines',
    label: t('CAMPAIGN.VOICE.DETAIL.RULES.LINES'),
    value: dialerConfig.value.max_concurrent_lines ?? 8,
  },
]);

const presence = useDialerPresenceStore();
const dialerState = ref({ queue: {}, agents: [] });
const isJoining = ref(false);
let pollTimer = null;

const mySession = computed(() =>
  (dialerState.value.agents || []).find(agent => agent.is_me)
);
const isInCampaign = computed(() => !!mySession.value);
const agents = computed(() => dialerState.value.agents || []);
const queue = computed(() => dialerState.value.queue || {});

const queueTiles = computed(() => [
  {
    key: 'pending',
    label: t('CAMPAIGN.VOICE.DETAIL.QUEUE.PENDING'),
    value: queue.value.pending ?? 0,
  },
  {
    key: 'dialing',
    label: t('CAMPAIGN.VOICE.DETAIL.QUEUE.DIALING'),
    value: queue.value.dialing ?? 0,
  },
  {
    key: 'connected',
    label: t('CAMPAIGN.VOICE.DETAIL.QUEUE.CONNECTED'),
    value: queue.value.connected ?? 0,
  },
  {
    key: 'completed',
    label: t('CAMPAIGN.VOICE.DETAIL.QUEUE.COMPLETED'),
    value: queue.value.completed ?? 0,
  },
  {
    key: 'no_answer',
    label: t('CAMPAIGN.VOICE.DETAIL.QUEUE.NO_ANSWER'),
    value: queue.value.no_answer ?? 0,
  },
]);

// The list pages and searches on its own; the screen tells it when the queue has moved.
const queueList = ref(null);
const refreshQueueList = () => queueList.value?.refresh();

const stats = computed(() => dialerState.value.stats || {});
const dialerStats = computed(() => stats.value.dialer || {});
const isRebuilding = ref(false);

// On or off comes from the state polled with everything else, never from the campaign loaded when
// the screen opened: a script turns a campaign on over the API, and that copy went on saying
// "paused" while it dialed. Unknown until the first poll answers.
const isEnabled = computed(() => dialerState.value.enabled);

// Why the campaign is standing still. Anything other than `ok` is shown, because a full queue that
// does not dial looks broken until the reason is on screen - except nobody being in it, which the
// agents card right above already says.
const dialStateMessage = computed(() => {
  const state = dialerState.value.dial_state;
  if (!state || state === 'ok') return null;
  if (state === 'no_agents' && !agents.value.length) return null;
  return t(`CAMPAIGN.VOICE.DETAIL.DIAL_STATE.${state.toUpperCase()}`);
});

const rebuildQueue = async () => {
  isRebuilding.value = true;
  try {
    const { data } = await DialerAPI.rebuild(route.params.campaignId);
    dialerState.value = data;
    refreshQueueList();
    useAlert(
      data.without_phone
        ? t('CAMPAIGN.VOICE.DETAIL.QUEUE.REBUILT_WITH_SKIPPED', {
            count: data.added,
            skipped: data.without_phone,
          })
        : t('CAMPAIGN.VOICE.DETAIL.QUEUE.REBUILT', { count: data.added })
    );
  } catch (error) {
    useAlert(t('CAMPAIGN.VOICE.DETAIL.QUEUE.REBUILD_FAILED'));
  } finally {
    isRebuilding.value = false;
  }
};

// Taking someone out and putting someone in: an audience is never exactly who you want to call,
// and until now the only way to fix it was to change the audience and rebuild.
const isAdding = ref(false);
const showAddForm = ref(false);
const newContact = ref({ name: '', phoneNumber: '' });
const removingTaskId = ref(null);

const addToQueue = async () => {
  if (!newContact.value.phoneNumber || isAdding.value) return;
  isAdding.value = true;
  try {
    const { data } = await DialerAPI.addToQueue(route.params.campaignId, {
      phoneNumber: newContact.value.phoneNumber,
      name: newContact.value.name,
    });
    dialerState.value = data;
    // The endpoint answers for each number on its own, so one refused number comes back in the body
    // rather than as a failed request.
    const [refused] = data.rejected || [];
    if (refused) {
      useAlert(refused.error || t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_FAILED'));
      return;
    }

    refreshQueueList();
    useAlert(
      t('CAMPAIGN.VOICE.DETAIL.LIST.ADDED', { name: data.added?.[0]?.name })
    );
    newContact.value = { name: '', phoneNumber: '' };
    showAddForm.value = false;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_FAILED')
    );
  } finally {
    isAdding.value = false;
  }
};

const removeFromQueue = async task => {
  removingTaskId.value = task.id;
  try {
    const { data } = await DialerAPI.removeFromQueue(
      route.params.campaignId,
      task.id
    );
    dialerState.value = data;
    refreshQueueList();
    useAlert(t('CAMPAIGN.VOICE.DETAIL.LIST.REMOVED', { name: task.name }));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.VOICE.DETAIL.LIST.REMOVE_FAILED')
    );
  } finally {
    removingTaskId.value = null;
  }
};

const fetchState = async () => {
  try {
    const { data } = await DialerAPI.state(route.params.campaignId);
    dialerState.value = data;
  } catch (error) {
    // A transient failure should not tear the panel down; the next poll picks it up.
  }
};

// Turning a campaign on used to take a PATCH over the API, so one created paused sat still with agents
// waiting in it and nobody on the screen able to start it. Pausing only stops new dials: the calls
// already in progress carry on.
const isToggling = ref(false);

const toggleEnabled = async () => {
  const enabled = !isEnabled.value;
  isToggling.value = true;
  try {
    await store.dispatch('campaigns/update', {
      id: route.params.campaignId,
      enabled,
    });
    useAlert(
      enabled
        ? t('CAMPAIGN.VOICE.DETAIL.TOGGLE.TURNED_ON')
        : t('CAMPAIGN.VOICE.DETAIL.TOGGLE.PAUSED')
    );
  } catch (error) {
    useAlert(t('CAMPAIGN.VOICE.DETAIL.TOGGLE.FAILED'));
  } finally {
    isToggling.value = false;
    fetchState();
  }
};

const joinCampaign = async () => {
  if (isJoining.value) return;
  isJoining.value = true;
  try {
    // The presence store owns the session from here on, so the agent can leave this screen and
    // keep taking calls: it parks the browser leg and keeps telling the server it is still there.
    const data = await presence.join({
      campaignId: route.params.campaignId,
      campaignTitle: campaign.value?.title,
    });
    dialerState.value = data;
    // Being in a campaign that is off is being in one that calls nobody, and saying it dials would
    // leave the agent waiting for a call that never comes.
    useAlert(
      data.dial_state === 'paused'
        ? t('CAMPAIGN.VOICE.DETAIL.AGENTS.JOINED_PAUSED')
        : t('CAMPAIGN.VOICE.DETAIL.AGENTS.JOINED')
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        // A microphone the browser refused is the common first-time failure, and the only one the
        // agent can do something about.
        t(
          presence.parkError === 'microphone'
            ? 'CAMPAIGN.VOICE.DETAIL.AGENTS.JOIN_FAILED_MICROPHONE'
            : 'CAMPAIGN.VOICE.DETAIL.AGENTS.JOIN_FAILED'
        )
    );
    await DialerAPI.leave(route.params.campaignId).catch(() => {});
  } finally {
    isJoining.value = false;
    fetchState();
  }
};

const leaveCampaign = async () => {
  try {
    await presence.leave();
  } finally {
    fetchState();
  }
};

// Keeping the leg alive belongs to the presence store, not to this screen: one that dies while the
// agent is working the inbox has to come back the same way it does here, and a check that only ran
// on this page left them in the rotation with nothing to hand a call to.
//
// What is left for the screen is the case the store cannot know about: the server has this agent in
// the campaign and *this* browser does not - they joined from another machine, or the session
// outlived the tab that made it. Then the panel would offer nothing but "leave", and the dialer
// would count an agent with no leg, so the screen that shows them in it makes it true again.
const adoptSession = async () => {
  if (isJoining.value || !isInCampaign.value) return;
  if (presence.campaignId === route.params.campaignId) return;

  await joinCampaign();
};

const pollState = async () => {
  await fetchState();
  await adoptSession();
};

onMounted(async () => {
  await fetchOwnCampaign();
  await pollState();
  pollTimer = setInterval(() => {
    pollState();
    refreshQueueList();
  }, STATE_POLL_MS);
});

// Leaving the page no longer leaves the campaign: the heartbeat keeps the agent present, and the
// server pauses them on its own the moment the browser stops reporting.
onBeforeUnmount(() => clearInterval(pollTimer));

const goBack = () =>
  router.push(
    frontendURL(`accounts/${route.params.accountId}/campaigns/voice`)
  );
</script>

<template>
  <div class="flex flex-col w-full h-full gap-6 px-6 py-6 overflow-y-auto">
    <div v-if="isFetching && !campaign" class="flex justify-center py-10">
      <Spinner />
    </div>

    <template v-else-if="campaign">
      <div class="flex items-start gap-4">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          @click="goBack"
        />
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex items-center gap-3">
            <h1 class="text-xl font-medium text-n-slate-12">
              {{ campaign.title }}
            </h1>
            <span
              v-if="isEnabled !== undefined"
              class="text-xs font-medium inline-flex items-center h-6 px-2 rounded-md"
              :class="
                isEnabled
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-slate-3 text-n-slate-11'
              "
            >
              {{
                isEnabled
                  ? t('CAMPAIGN.VOICE.CARD.STATUS.ACTIVE')
                  : t('CAMPAIGN.VOICE.CARD.STATUS.PAUSED')
              }}
            </span>
          </div>
          <div class="flex items-center gap-2 text-sm text-n-slate-11">
            <Icon icon="i-lucide-phone" class="size-4" />
            <span>{{ campaign.voice_phone_number }}</span>
            <span class="text-n-slate-8">·</span>
            <span>{{ audienceSummary }}</span>
          </div>
        </div>
        <Button
          v-if="isAdmin && isEnabled !== undefined"
          :label="
            isEnabled
              ? t('CAMPAIGN.VOICE.DETAIL.TOGGLE.PAUSE')
              : t('CAMPAIGN.VOICE.DETAIL.TOGGLE.TURN_ON')
          "
          :icon="isEnabled ? 'i-lucide-pause' : 'i-lucide-play'"
          :variant="isEnabled ? 'faded' : 'solid'"
          :color="isEnabled ? 'slate' : 'blue'"
          size="sm"
          :is-loading="isToggling"
          @click="toggleEnabled"
        />
      </div>

      <!-- The agent side of the campaign: joining is what authorises dialing, and it is the part
           the dialer runtime still owes. -->
      <div
        class="flex flex-col gap-3 p-5 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col gap-1">
            <span class="text-base font-medium text-n-slate-12">
              {{ t('CAMPAIGN.VOICE.DETAIL.AGENTS.TITLE') }}
            </span>
            <span v-if="!agents.length" class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.VOICE.DETAIL.AGENTS.EMPTY') }}
            </span>
            <div v-else class="flex flex-wrap items-center gap-2">
              <span
                v-for="agent in agents"
                :key="agent.id"
                class="inline-flex items-center gap-2 px-2 py-1 text-sm rounded-md bg-n-alpha-2 text-n-slate-12"
              >
                {{ agent.name }}
                <span class="text-xs text-n-slate-11">
                  {{
                    t(
                      `CAMPAIGN.VOICE.DETAIL.AGENTS.STATUS.${agent.status.toUpperCase()}`
                    )
                  }}
                </span>
              </span>
            </div>
          </div>
          <Button
            v-if="isInCampaign"
            :label="t('CAMPAIGN.VOICE.DETAIL.AGENTS.LEAVE')"
            variant="faded"
            color="ruby"
            size="sm"
            @click="leaveCampaign"
          />
          <Button
            v-else
            :label="t('CAMPAIGN.VOICE.DETAIL.AGENTS.JOIN')"
            size="sm"
            :is-loading="isJoining"
            @click="joinCampaign"
          />
        </div>
      </div>

      <div
        v-if="dialStateMessage"
        class="flex items-center gap-2 px-4 py-3 text-sm border rounded-xl border-n-amber-6 bg-n-amber-2 text-n-amber-11"
      >
        <Icon icon="i-lucide-info" class="flex-shrink-0 size-4" />
        <span>{{ dialStateMessage }}</span>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3">
          <span class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.VOICE.DETAIL.QUEUE.TITLE') }}
          </span>
          <Button
            v-if="isAdmin"
            :label="t('CAMPAIGN.VOICE.DETAIL.QUEUE.REBUILD')"
            variant="faded"
            color="slate"
            size="sm"
            :is-loading="isRebuilding"
            @click="rebuildQueue"
          />
        </div>
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <div
            v-for="tile in queueTiles"
            :key="tile.key"
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <span class="text-sm text-n-slate-11">{{ tile.label }}</span>
            <span class="text-xl font-medium text-n-slate-12">
              {{ tile.value }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <div class="flex items-baseline gap-3">
          <span class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.VOICE.DETAIL.STATS.TITLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              t('CAMPAIGN.VOICE.DETAIL.STATS.DIALER_SUMMARY', {
                attempts: dialerStats.attempts || 0,
                abandoned: dialerStats.abandoned || 0,
                voicemail: dialerStats.voicemail || 0,
              })
            }}
          </span>
        </div>
        <CallStatsPanel :stats="stats" />
      </div>

      <!-- How the campaign ran, step by step: for whoever manages it, like the endpoints behind it. -->
      <VoiceCampaignDiagnostics
        v-if="isAdmin"
        :campaign-id="route.params.campaignId"
      />

      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3">
          <span class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.VOICE.DETAIL.LIST.TITLE') }}
          </span>
          <Button
            v-if="isAdmin"
            :label="t('CAMPAIGN.VOICE.DETAIL.LIST.ADD')"
            icon="i-lucide-plus"
            variant="faded"
            color="slate"
            size="sm"
            @click="showAddForm = !showAddForm"
          />
        </div>

        <div
          v-if="showAddForm"
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1 sm:flex-row sm:items-end"
        >
          <Input
            v-model="newContact.name"
            :label="t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_NAME')"
            :placeholder="t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_NAME_PLACEHOLDER')"
            size="sm"
            class="flex-1"
          />
          <Input
            v-model="newContact.phoneNumber"
            :label="t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_PHONE')"
            placeholder="+5511999999999"
            size="sm"
            class="flex-1"
            @enter="addToQueue"
          />
          <Button
            :label="t('CAMPAIGN.VOICE.DETAIL.LIST.ADD_SUBMIT')"
            size="sm"
            :is-loading="isAdding"
            :disabled="!newContact.phoneNumber"
            @click="addToQueue"
          />
        </div>

        <VoiceCampaignQueueList
          ref="queueList"
          :campaign-id="route.params.campaignId"
          :can-remove="isAdmin"
          :removing-task-id="removingTaskId"
          @remove="removeFromQueue"
        />
      </div>

      <div class="flex flex-col gap-3">
        <span class="text-base font-medium text-n-slate-12">
          {{ t('CAMPAIGN.VOICE.DETAIL.RULES.TITLE') }}
        </span>
        <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div
            v-for="rule in rules"
            :key="rule.key"
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <span class="text-sm text-n-slate-11">{{ rule.label }}</span>
            <span class="text-xl font-medium text-n-slate-12">
              {{ rule.value }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="pt-14 text-center text-n-slate-11">
      {{ t('CAMPAIGN.VOICE.DETAIL.NOT_FOUND') }}
    </div>
  </div>
</template>
